import { ShigoDashboardContext } from '../../auth/dashboardDestination';
import { NetworkAvailableFeature } from '../../auth/networkFeatures';
import { reportDimensionSections, reportMetricDefinitions, reportMetricSections } from './catalog';
import { ReportCatalogGroup, ReportDimension, ReportMetric } from './reportEnums';

export function availableMetricSections(
  features: readonly NetworkAvailableFeature[],
  dashboardContext = ShigoDashboardContext.Main,
) {
  if (dashboardContext === ShigoDashboardContext.Agency) {
    return agencyMetricSections;
  }
  if (dashboardContext === ShigoDashboardContext.Publisher) {
    return publisherMetricSections;
  }
  const featureSet = new Set(features);
  return reportMetricSections.flatMap((section) => {
    if (
      dashboardContext !== ShigoDashboardContext.SuperAdmin &&
      section.group === ReportCatalogGroup.Network
    )
      return [];
    if (
      dashboardContext !== ShigoDashboardContext.SuperAdmin &&
      section.group === ReportCatalogGroup.Stitcher &&
      !featureSet.has(NetworkAvailableFeature.Stitcher)
    )
      return [];
    if (
      dashboardContext !== ShigoDashboardContext.SuperAdmin &&
      (section.group === ReportCatalogGroup.Campaigns ||
        section.group === ReportCatalogGroup.Content ||
        section.group === ReportCatalogGroup.WebVideo) &&
      !featureSet.has(NetworkAvailableFeature.Campaign)
    )
      return [];
    const items = section.items.filter(
      (item) =>
        !hiddenIntentIqMetrics.has(item.metric) &&
        !hiddenMetrics(dashboardContext, section.group).has(item.metric) &&
        (dashboardContext === ShigoDashboardContext.SuperAdmin ||
          !item.requiredFeature ||
          featureSet.has(item.requiredFeature)),
    );
    return items.length ? [{ ...section, items }] : [];
  });
}

export function availableDimensionSections(
  features: readonly NetworkAvailableFeature[],
  dashboardContext = ShigoDashboardContext.Main,
) {
  if (dashboardContext === ShigoDashboardContext.Agency) {
    return reportDimensionSections.flatMap((section) => {
      const items = section.items.filter(({ dimension }) => agencyDimensions.has(dimension));
      return items.length ? [{ ...section, items }] : [];
    });
  }
  if (dashboardContext === ShigoDashboardContext.Publisher) {
    return reportDimensionSections.flatMap((section) => {
      const items = section.items.filter(({ dimension }) => publisherDimensions.has(dimension));
      return items.length ? [{ ...section, items }] : [];
    });
  }
  const hidden = hiddenDimensions(dashboardContext);
  const featureSet = new Set(features);
  return reportDimensionSections.flatMap((section) => {
    const items = section.items.filter(
      (item) =>
        !hiddenIntentIqDimensions.has(item.dimension) &&
        !hidden.has(item.dimension) &&
        (dashboardContext === ShigoDashboardContext.SuperAdmin ||
          !item.requiredFeature ||
          featureSet.has(item.requiredFeature)),
    );
    return items.length ? [{ ...section, items }] : [];
  });
}

function hiddenMetrics(
  dashboardContext: ShigoDashboardContext,
  group: ReportCatalogGroup,
): ReadonlySet<ReportMetric> {
  if (group === ReportCatalogGroup.Kpi) return hiddenKpiMetrics;
  if (group === ReportCatalogGroup.Exchange) {
    return dashboardContext === ShigoDashboardContext.SuperAdmin
      ? hiddenSuperAdminExchangeMetrics
      : hiddenMainExchangeMetrics;
  }
  if (group === ReportCatalogGroup.Finance) {
    return dashboardContext === ShigoDashboardContext.SuperAdmin
      ? hiddenSuperAdminFinanceMetrics
      : hiddenMainFinanceMetrics;
  }
  return emptyMetrics;
}

function hiddenDimensions(dashboardContext: ShigoDashboardContext) {
  return dashboardContext === ShigoDashboardContext.SuperAdmin
    ? hiddenSuperAdminDimensions
    : hiddenMainDimensions;
}

// Keep IntentIQ definitions and request support while its report options are hidden.
const hiddenIntentIqMetrics = new Set([
  ReportMetric.IiqResolvedRequests,
  ReportMetric.IiqAdImpressions,
  ReportMetric.IiqRevenue,
  ReportMetric.IiqArm,
  ReportMetric.Browser,
]);
const hiddenIntentIqDimensions = new Set([ReportDimension.IiqArm, ReportDimension.Browser]);

const emptyMetrics = new Set<ReportMetric>();
const hiddenKpiMetrics = new Set([ReportMetric.AdImpressionsDvIqm]);
const hiddenMainExchangeMetrics = new Set([ReportMetric.LostRatio, ReportMetric.BidWinLostRatio]);
const hiddenSuperAdminExchangeMetrics = new Set([
  ...hiddenMainExchangeMetrics,
  ReportMetric.DpAdRequests,
]);
// Network-owned finance metrics stay out of Finance when their dashboard context exposes
// them in Network; Main also hides metrics that are unavailable without that section.
const hiddenMainFinanceMetrics = new Set([
  ReportMetric.Profit,
  ReportMetric.NetRevenue,
  ReportMetric.PubGrossRevenue,
  ReportMetric.NetworkSaasFee,
  ReportMetric.MarginPct,
]);
const hiddenSuperAdminFinanceMetrics = new Set([
  ReportMetric.NetRevenue,
  ReportMetric.PubGrossRevenue,
  ReportMetric.NetworkMarginPct,
  ReportMetric.NetworkSaasFee,
  ReportMetric.NetworkRevenue,
  ReportMetric.NetworkProfit,
]);
const hiddenMainDimensions = new Set([
  ReportDimension.Network,
  ReportDimension.ErrorCode,
  ReportDimension.DvSegment,
]);
const hiddenSuperAdminDimensions = new Set([ReportDimension.ErrorCode, ReportDimension.DvSegment]);
const publisherDimensions = new Set([
  ReportDimension.Year,
  ReportDimension.Month,
  ReportDimension.Day,
  ReportDimension.Hour,
  ReportDimension.Publisher,
  ReportDimension.PublisherDomain,
  ReportDimension.App,
  ReportDimension.Placement,
]);
const publisherMetricSections = [
  metricSection(ReportCatalogGroup.General, [
    ReportMetric.PublisherName,
    ReportMetric.Domain,
    ReportMetric.AppBundleId,
    ReportMetric.Placement,
    ReportMetric.PlacementId,
  ]),
  metricSection(ReportCatalogGroup.Kpi, [
    ReportMetric.AdRequests,
    ReportMetric.FilledImpressions,
    ReportMetric.PubRenderRate,
    ReportMetric.PubAdImpressions,
  ]),
  metricSection(ReportCatalogGroup.Finance, [
    ReportMetric.PublisherRevenueShareIo,
    ReportMetric.PublisherCpm,
    ReportMetric.PubRevenue,
  ]),
] as const;

function metricSection(group: ReportCatalogGroup, metrics: readonly ReportMetric[]) {
  return {
    group,
    labelKey: `reportExplorer.group.${group}` as const,
    items: metrics.map((metric) => reportMetricDefinitions[metric]),
  };
}

const agencyMetricSections = [
  metricSection(ReportCatalogGroup.General, [
    ReportMetric.AgencyId,
    ReportMetric.AgencyName,
    ReportMetric.BrandId,
    ReportMetric.BrandName,
    ReportMetric.Order,
    ReportMetric.LineItem,
    ReportMetric.LineItemId,
    ReportMetric.CreativeId,
    ReportMetric.CreativeName,
    ReportMetric.CrId,
    ReportMetric.PublisherId,
    ReportMetric.PublisherName,
    ReportMetric.PlacementId,
    ReportMetric.Placement,
    ReportMetric.AppId,
    ReportMetric.AppName,
    ReportMetric.AppBundleId,
  ]),
  metricSection(ReportCatalogGroup.Campaigns, [
    ReportMetric.AdImpressionsLi,
    ReportMetric.FilledImpressionsLi,
    ReportMetric.RenderRateLi,
    ReportMetric.AdClicks,
    ReportMetric.Ctr,
    ReportMetric.AdStart,
    ReportMetric.AdMid,
    ReportMetric.AdCompletion,
    ReportMetric.View,
    ReportMetric.ViewPct,
    ReportMetric.RevenueLi,
    ReportMetric.RpmLi,
  ]),
];
const agencyDimensions = new Set([
  ReportDimension.Day,
  ReportDimension.Hour,
  ReportDimension.Agency,
  ReportDimension.Brand,
  ReportDimension.Order,
  ReportDimension.LineItem,
  ReportDimension.Creative,
  ReportDimension.CrId,
  ReportDimension.Publisher,
  ReportDimension.Placement,
  ReportDimension.App,
]);
